import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, X, Search } from "lucide-react";

/**
 * Modal that lets staff pick a previously uploaded image (or upload a new one)
 * and returns the public URL.
 */
export default function MediaPicker({ open, onClose, onPick, accept = "image/*" }) {
  const { authedClient } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState("");

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const { data } = await authedClient().get("/admin/uploads");
      setAssets(data);
    } catch {
      toast.error("Could not load media library");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchAssets();
  }, [open]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await authedClient().post("/admin/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      setAssets((prev) => [data, ...prev]);
      onPick?.(data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (!open) return null;

  const filtered = assets.filter(
    (a) =>
      !query ||
      a.original_filename.toLowerCase().includes(query.toLowerCase()) ||
      (a.label || "").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      data-testid="media-picker-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-[color:var(--line)]">
          <h3 className="font-display text-xl text-[color:var(--blue-ink)]">Select image</h3>
          <button
            onClick={onClose}
            data-testid="media-picker-close"
            className="p-2 rounded-full hover:bg-[color:var(--cream)]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 flex flex-wrap gap-3 items-center border-b border-[color:var(--line)]">
          <label className="btn-primary cursor-pointer">
            <Upload className="w-4 h-4" />
            {uploading ? "Uploading…" : "Upload new"}
            <input
              type="file"
              accept={accept}
              hidden
              onChange={handleUpload}
              disabled={uploading}
              data-testid="media-picker-upload-input"
            />
          </label>
          <div className="flex-1 min-w-[200px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--muted)]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search filename or label"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-[color:var(--line)] focus:outline-none focus:border-[color:var(--blue)]"
              data-testid="media-picker-search"
            />
          </div>
        </div>

        <div className="overflow-y-auto p-5 flex-1">
          {loading ? (
            <p className="text-center text-[color:var(--muted)]">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-[color:var(--muted)]">
              No assets yet. Upload one to get started.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {filtered.map((a) => (
                <button
                  key={a.id}
                  data-testid={`media-pick-${a.id}`}
                  onClick={() => {
                    onPick?.(a);
                    onClose?.();
                  }}
                  className="group relative aspect-square overflow-hidden rounded-lg border border-[color:var(--line)] hover:border-[color:var(--blue)] transition"
                >
                  {a.content_type?.startsWith("image/") ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${a.public_url}`}
                      alt={a.label || a.original_filename}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-[color:var(--muted)] text-xs">
                      {a.content_type}
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-xs px-2 py-1 truncate opacity-0 group-hover:opacity-100 transition">
                    {a.label || a.original_filename}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
